import { Expense } from "@/types/expense";
import { motion } from "framer-motion";
import { 
  IndianRupee, 
  Calendar, 
  Users,
  TrendingUp,
  PieChart, 
  BarChart, 
  LineChart,
  Receipt,
  Wallet,
  ArrowUpCircle
} from "lucide-react";
import { CategoryPieChart } from "./charts/CategoryPieChart";
import { PaymentBarChart } from "./charts/PaymentBarChart";
import { ExpenseDateChart } from "./charts/ExpenseDateChart";
import { InsightCard } from "./insights/InsightCard";
import { ChartSection } from "./insights/ChartSection";
import { EmptyInsights } from "./insights/EmptyInsights";
import { ScrollArea } from "@/components/ui/scroll-area";

interface TripInsightsProps {
  expenses: Expense[];
}

export const TripInsights = ({ expenses }: TripInsightsProps) => {
  const totalExpenses = expenses.reduce((sum, expense) => sum + Number(expense.amount), 0);
  const startDate = expenses[0]?.date ? new Date(expenses[0].date) : new Date();
  const endDate = expenses[expenses.length - 1]?.date ? new Date(expenses[expenses.length - 1].date) : new Date();
  const tripDuration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  const uniqueParticipants = new Set(expenses.flatMap(e => e.participants || []));
  const avgExpensePerDay = tripDuration > 0 ? totalExpenses / tripDuration : totalExpenses;
  
  const highestExpense = expenses.length > 0 
    ? expenses.reduce((max, exp) => Number(exp.amount) > Number(max.amount) ? exp : max)
    : null;
  
  const categoryTotals = expenses.reduce((acc, exp) => {
    acc[exp.category] = (acc[exp.category] || 0) + Number(exp.amount);
    return acc;
  }, {} as Record<string, number>);
  
  const topCategory = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1])[0];

  if (expenses.length === 0) {
    return <EmptyInsights />;
  }

  return (
    <div className="space-y-6 p-2 md:p-4">
      <ScrollArea className="w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 min-w-[300px]">
          <InsightCard
            icon={IndianRupee}
            iconColor="text-purple-500"
            title="Total Spent"
            value={`₹${totalExpenses.toFixed(2)}`}
            description="Total expenses across all categories"
            gradientFrom="purple-50"
            gradientTo="purple-100"
            delay={0.1}
          />
          
          <InsightCard
            icon={Calendar}
            iconColor="text-blue-500"
            title="Trip Duration"
            value={`${tripDuration} days`}
            description="Length of your journey"
            gradientFrom="blue-50"
            gradientTo="blue-100"
            delay={0.2}
          />
          
          <InsightCard
            icon={Users}
            iconColor="text-green-500"
            title="Participants"
            value={uniqueParticipants.size}
            description="People sharing expenses"
            gradientFrom="green-50"
            gradientTo="green-100"
            delay={0.3}
          />

          <InsightCard
            icon={TrendingUp}
            iconColor="text-orange-500"
            title="Avg. per Day"
            value={`₹${avgExpensePerDay.toFixed(2)}`}
            description="Daily spending average"
            gradientFrom="orange-50"
            gradientTo="orange-100"
            delay={0.4}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <InsightCard
            icon={ArrowUpCircle}
            iconColor="text-pink-500"
            title="Highest Expense"
            value={highestExpense ? `₹${Number(highestExpense.amount).toFixed(2)}` : "N/A"}
            description={highestExpense?.description || "No expenses yet"}
            gradientFrom="pink-50"
            gradientTo="pink-100"
            delay={0.5}
          />

          <InsightCard
            icon={Wallet}
            iconColor="text-yellow-500"
            title="Top Category"
            value={topCategory ? topCategory[0] : "N/A"}
            description={topCategory ? `₹${topCategory[1].toFixed(2)} spent` : "No expenses yet"}
            gradientFrom="yellow-50"
            gradientTo="yellow-100"
            delay={0.6}
          />
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.7 }}
          className="space-y-6 mt-6"
        >
          <ChartSection
            icon={LineChart}
            iconColor="text-blue-500"
            title="Daily Expense Trends"
            description="Track your spending patterns over time"
          >
            <ExpenseDateChart expenses={expenses} />
          </ChartSection>

          <ChartSection
            icon={PieChart}
            iconColor="text-purple-500"
            title="Expenses by Category"
            description="See how your money is distributed"
          >
            <CategoryPieChart expenses={expenses} totalExpenses={totalExpenses} />
          </ChartSection>

          <ChartSection
            icon={BarChart}
            iconColor="text-green-500"
            title="Payment Distribution"
            description="Understand who paid for what"
          >
            <PaymentBarChart expenses={expenses} />
          </ChartSection>
        </motion.div>
      </ScrollArea>
    </div>
  );
};
